import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Check, Eye, EyeOff, Loader2, X } from 'lucide-react';
import { useAuth } from '../../../auth/AuthContext';
import { requestAccountDeletion, WrongPasswordError } from '../../../services/accountService';

// The confirm for deleting an account (#452): it says what is erased and what
// stays public, and asks for the password again. A brand owner's account
// locks now and is purged in 30 days (the app then shows the restore
// screen); a team member's seat goes at once.

interface Props {
  isMember: boolean;
  /** The brand: the owner's published name, or the team's for a member. */
  brandName?: string;
  onClose: () => void;
}

const ListItem: React.FC<{ kept?: boolean; children: React.ReactNode }> = ({ kept, children }) => (
  <li className="flex items-start gap-2">
    {kept ? (
      <Check size={15} className="mt-0.5 shrink-0 text-william-600 dark:text-william-300" aria-hidden="true" />
    ) : (
      <X size={15} className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" aria-hidden="true" />
    )}
    <span>{children}</span>
  </li>
);

const DeleteAccountDialog: React.FC<Props> = ({ isMember, brandName, onClose }) => {
  const { userProfile, refetchProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const name = brandName || 'your brand';
  const team = brandName ? `${brandName}'s team` : 'the team';
  const title = isMember ? `Leave ${team}?` : `Delete ${name}'s account?`;
  const plan = userProfile?.planName;
  const left = userProfile?.tokenBalance ?? 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await requestAccountDeletion(password);
      if (result.kind === 'scheduled') {
        // The profile now carries the pending deletion: the app shows the restore screen.
        await refetchProfile();
        onClose();
      } else {
        await logout({ everywhere: false });
        navigate('/login', { replace: true, state: { notice: 'account-deleted' } });
      }
    } catch (err) {
      setError(err instanceof WrongPasswordError ? err.message : (err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} />
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.15 }}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-account-title"
        className="relative bg-white dark:bg-night-800 rounded-2xl shadow-xl max-w-lg w-full max-h-[calc(100vh-2rem)] overflow-y-auto p-6 flex flex-col gap-5"
      >
        <h3 id="delete-account-title" className="text-lg font-semibold text-gray-900 dark:text-white break-words">
          {title}
        </h3>

        {isMember ? (
          <ul className="space-y-1.5 text-sm text-gray-600 dark:text-gray-300">
            <ListItem>Your sign-in, name and email are erased.</ListItem>
            <ListItem>Your API keys and connected apps stop working at once.</ListItem>
            <ListItem kept>The collections, products and items you made stay with {brandName ?? 'the team'}.</ListItem>
          </ul>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 text-sm text-gray-600 dark:text-gray-300">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Erased in 30 days
              </p>
              <ul className="space-y-1.5">
                <ListItem>Your sign-in, email and name</ListItem>
                <ListItem>Your team members and their sign-ins</ListItem>
                <ListItem>API keys and connected apps (stopped today)</ListItem>
                <ListItem>Line sheets and who they were sent to (stopped today)</ListItem>
                <ListItem>Your wallet key and personal logs</ListItem>
              </ul>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Stays online
              </p>
              <ul className="space-y-1.5">
                <ListItem kept>Product pages, under “{name}”</ListItem>
                <ListItem kept>Every printed QR code, redirect codes too</ListItem>
                <ListItem kept>Photos and logo on those pages</ListItem>
                <ListItem kept>Records on the public ledger</ListItem>
              </ul>
            </div>
          </div>
        )}

        {!isMember && (
          <div className="rounded-xl bg-gray-50 dark:bg-white/[0.04] px-4 py-3 text-sm text-gray-600 dark:text-gray-300 space-y-1">
            {plan && <p>Your {plan} plan stops renewing. You keep it until the end of the period already paid.</p>}
            {left > 0 && <p>Your {left} styles and codes left are lost.</p>}
            <p>
              Changed your mind? <b className="font-semibold text-gray-900 dark:text-white">Sign in within 30 days</b> to restore the account.
              Download your data first if you want a copy.
            </p>
          </div>
        )}

        <label className="block">
          <span className="text-sm font-medium text-gray-900 dark:text-white">Your password</span>
          <span className="relative mt-1.5 block">
            <input
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              autoFocus
              aria-invalid={!!error}
              className="w-full rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-night-900 px-3.5 py-2.5 pr-11 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500/40"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              aria-label={show ? 'Hide password' : 'Show password'}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </span>
          {error && (
            <span className="mt-1.5 block text-sm text-red-600 dark:text-red-400" role="alert">
              {error}
            </span>
          )}
        </label>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex-1 px-4 py-2.5 text-sm font-medium rounded-xl border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !password}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {isMember ? 'Leave the team' : 'Delete account'}
          </button>
        </div>
      </motion.form>
    </div>
  );
};

export default DeleteAccountDialog;
